import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Send, MoreVertical, Heart, Flag, ShieldOff, HeartOff } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { apiService, formatApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import ReportUserModal from './ReportUserModal';
import { resolvePhotoUrl } from '../utils/photoUrl';

const Avatar = ({ src, name, className }) => {
  const url = resolvePhotoUrl(src);
  return url ? <img src={url} alt={name} className={`${className} object-cover`} />
      : <div className={`${className} bg-gradient-to-br from-pink-200 to-red-200 flex items-center justify-center text-red-600 font-semibold`}>{(name || '?')[0]}</div>;
};

const formatTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const ChatView = ({ match, onBack, onMatchRemoved }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await apiService.messages.getMessages(match.id);
      setMessages(res.data.data || []);
      setLoaded(true);
    } catch (e) {
      if (e.response?.status === 404) onMatchRemoved(match.id);
    }
  }, [match.id, onMatchRemoved]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async () => {
    const text = newMessage.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await apiService.messages.sendMessage(match.id, text);
      setMessages((prev) => [...prev, res.data.data]);
      setNewMessage('');
    } catch (e) {
      toast({ title: 'Message not sent', description: formatApiError(e), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const unmatch = async () => {
    try {
      await apiService.swipes.unmatch(match.id);
      toast({ title: 'Unmatched', description: `You unmatched ${match.name}.` });
      onMatchRemoved(match.id);
    } catch (e) {
      toast({ title: 'Could not unmatch', description: formatApiError(e), variant: 'destructive' });
    }
  };

  const block = async () => {
    try {
      await apiService.safety.block(match.user_id);
      toast({ title: 'Blocked', description: `${match.name} can no longer contact you.` });
      onMatchRemoved(match.id);
    } catch (e) {
      toast({ title: 'Could not block', description: formatApiError(e), variant: 'destructive' });
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-180px)]" data-testid="chat-view">
      <div className="flex items-center justify-between p-4 bg-white border-b sticky top-0">
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="chat-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <Avatar src={match.image} name={match.name} className="w-10 h-10 rounded-full" />
          <h3 className="font-semibold text-gray-900" data-testid="chat-partner-name">{match.name}</h3>
        </div>
        <div className="relative">
          <Button variant="ghost" size="sm" onClick={() => setMenuOpen((o) => !o)} data-testid="chat-menu-btn"><MoreVertical className="w-5 h-5 text-gray-600" /></Button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-44 bg-white border rounded-lg shadow-lg z-20 text-sm" data-testid="chat-menu">
              <button className="w-full flex items-center px-3 py-2 hover:bg-gray-50" onClick={() => { setMenuOpen(false); setShowReport(true); }} data-testid="chat-report-btn"><Flag className="w-4 h-4 mr-2 text-red-500" />Report</button>
              <button className="w-full flex items-center px-3 py-2 hover:bg-gray-50" onClick={() => { setMenuOpen(false); block(); }} data-testid="chat-block-btn"><ShieldOff className="w-4 h-4 mr-2 text-gray-600" />Block</button>
              <button className="w-full flex items-center px-3 py-2 hover:bg-gray-50" onClick={() => { setMenuOpen(false); unmatch(); }} data-testid="chat-unmatch-btn"><HeartOff className="w-4 h-4 mr-2 text-gray-600" />Unmatch</button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
        {!loaded ? (
          <p className="text-center text-gray-400 py-8" data-testid="chat-loading">Loading conversation…</p>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <Heart className="w-12 h-12 mx-auto text-pink-300 mb-3" />
            <p className="text-gray-500">You matched! Start the conversation.</p>
          </div>
        ) : messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${mine ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white' : 'bg-gray-100 text-gray-900'}`}>
                <p className="text-sm whitespace-pre-wrap break-words">{m.message}</p>
                <p className={`text-xs mt-1 ${mine ? 'text-pink-100' : 'text-gray-500'}`}>{formatTime(m.timestamp)}</p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="p-4 bg-white border-t">
        <div className="flex items-center space-x-3">
          <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value.slice(0, 2000))} placeholder="Type a message..."
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()} data-testid="message-input" />
          <Button onClick={send} className="bg-gradient-to-r from-pink-500 to-red-500 text-white" disabled={!newMessage.trim() || sending} data-testid="message-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {showReport && <ReportUserModal user={{ id: match.user_id, name: match.name }} onClose={() => setShowReport(false)} onDone={({ blocked }) => blocked && onMatchRemoved(match.id)} />}
    </div>
  );
};

const Messages = ({ initialMatchId = null }) => {
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingOpen, setPendingOpen] = useState(initialMatchId);

  const loadMatches = useCallback(() => {
    setLoading(true);
    setError('');
    apiService.swipes.getMatches()
      .then((res) => setMatches(res.data.data || []))
      .catch(() => setError("We couldn't load your conversations. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadMatches(); }, [loadMatches]);

  useEffect(() => {
    if (!pendingOpen || loading) return;
    const target = matches.find((m) => m.id === pendingOpen);
    if (target) setSelected(target);
    setPendingOpen(null);
  }, [pendingOpen, loading, matches]);

  const onMatchRemoved = useCallback((id) => {
    setMatches((prev) => prev.filter((m) => m.id !== id));
    setSelected(null);
  }, []);

  if (selected) return <ChatView match={selected} onBack={() => { setSelected(null); loadMatches(); }} onMatchRemoved={onMatchRemoved} />;
  if (loading) return <div className="text-center py-12 text-gray-500" data-testid="messages-loading">Loading conversations…</div>;
  if (error) return (
    <div className="text-center py-12" data-testid="messages-error">
      <p className="text-red-600 font-medium mb-4">{error}</p>
      <Button onClick={loadMatches} data-testid="messages-error-retry-btn">Try again</Button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="messages-screen">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Messages</h1>
        <p className="text-gray-600">Connect with your matches</p>
      </div>
      <div className="space-y-3">
        {matches.map((match) => (
          <Card key={match.id} className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelected(match)} data-testid={`conversation-${match.user_id}`}>
            <div className="flex items-center space-x-4">
              <Avatar src={match.image} name={match.name} className="w-14 h-14 rounded-full" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 truncate">{match.name}</h3>
                  <span className="text-xs text-gray-500">{formatTime(match.timestamp)}</span>
                </div>
                <p className="text-sm text-gray-600 truncate mt-1">{match.last_message || 'Say hello 👋'}</p>
              </div>
              {match.unread_count > 0 && <Badge className="bg-red-500 text-white">{match.unread_count}</Badge>}
            </div>
          </Card>
        ))}
      </div>
      {matches.length === 0 && (
        <div className="text-center py-12" data-testid="messages-empty">
          <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No conversations yet</h3>
          <p className="text-gray-500">Start matching to begin conversations!</p>
        </div>
      )}
    </div>
  );
};

export default Messages;
